import { useMemo } from 'react';

import { type ConversationAction } from '@atlassian/rovo-conversation-actions';

import { fg } from '@confluence/feature-gating';
import {
	isNudgeByAction,
	type NudgeEntry,
} from '@confluence/page-rovo-config-common/entry-points/nudgeTypes.ts';
import {
	type AgentMentionContext,
	useAgentMentionNudgeAction,
} from '@confluence/page-rovo-config-common/entry-points/useAgentMentionNudgeAction.ts';
import { usePageAgentSessionNudges } from '@confluence/page-rovo-config-common/entry-points/usePageAgentSessionNudges.ts';
import { useViewPageRovoActions } from '@confluence/page-rovo-config-common/entry-points/useViewPageRovoActions.ts';

const AUDIO_BRIEFING_MIN_READ_TIME_MINUTES = 5;

export type EditPageNudgeConfigResult = Readonly<{
	errors: readonly Error[];
	nudges: ConversationAction[];
}>;

/**
 * Returns the final nudge configuration for the edit page: agent-session nudges followed by the
 * agent mention and live-doc nudges.
 */
export function useEditPageNudgeConfig(
	enableLiveDocNudges: boolean,
	eligibleForChangeboarding: boolean,
	isFirstVisit: boolean,
	isCurrentUserContributor: boolean,
	isEligibilityLoading: boolean,
	readTimeMinutes: number | null,
	/**
	 * controls nudge *presence* (truthy = mention exists). Presence also requires
	 * `resolvedAgentMentionId` below, so the nudge never renders (and can't be clicked) before
	 * the Agent Studio UUID has resolved — clicking before resolution would fire `chat-new`
	 * without a routable agentId.
	 */
	agentMentionId?: string | null,
	/** control nudge *presence* (see above) and *payload* (forwarded to useAgentMentionNudgeAction). */
	resolvedAgentMentionId?: string | null,
	/** control nudge *payload* (forwarded to useAgentMentionNudgeAction). */
	agentMentionContext?: AgentMentionContext | null,
	/** control nudge fallback prompt (forwarded to useAgentMentionNudgeAction). */
	agentMentionPrompt?: string | null,
	/** controls the nudge action id so each inserted mention is treated as a new nudge. */
	agentMentionInsertionCount?: number,
	/** control nudge title (forwarded to useAgentMentionNudgeAction). */
	agentMentionName?: string | null,
	/** invoking mention node localId (forwarded to seed the agent-run-state bridge). */
	invokedByNodeLocalId?: string | null,
	/** current page ID used to retrieve page-scoped agent-session nudges. */
	contentId = '',
): EditPageNudgeConfigResult {
	const viewPageRovoActions = useViewPageRovoActions();
	const { nudges: sessionNudges, errors: sessionErrors } = usePageAgentSessionNudges({
		contentId,
		enabled: true,
	});
	const agentMentionNudgeAction = useAgentMentionNudgeAction(
		resolvedAgentMentionId,
		agentMentionContext,
		agentMentionPrompt,
		agentMentionInsertionCount,
		agentMentionName,
		agentMentionId,
		invokedByNodeLocalId,
	);

	const actionsById = useMemo(
		() =>
			new Map<string, ConversationAction>(
				viewPageRovoActions.map(
					(registeredAction: { actionId: string; action: ConversationAction }) => [
						registeredAction.actionId,
						registeredAction.action,
					],
				),
			),
		[viewPageRovoActions],
	);

	const nudges = useMemo(() => {
		// Require resolvedAgentMentionId too: without it, a click fired before the AAID→UUID
		// resolution completes would publish `chat-new` with no agentId, silently routing to
		// the default/current conversation agent instead of the mentioned one.
		const alwaysOnNudges: ConversationAction[] = [
			...sessionNudges,
			...(agentMentionId && resolvedAgentMentionId ? [agentMentionNudgeAction] : []),
		];

		const contentReviewerNudge = actionsById.get('content-reviewer-nudge');
		if (contentReviewerNudge) {
			alwaysOnNudges.push(contentReviewerNudge);
		}

		if (!enableLiveDocNudges) return alwaysOnNudges;

		const isAudioBriefingEligible =
			!isEligibilityLoading &&
			!eligibleForChangeboarding &&
			isFirstVisit &&
			!isCurrentUserContributor &&
			readTimeMinutes !== null &&
			readTimeMinutes >= AUDIO_BRIEFING_MIN_READ_TIME_MINUTES;

		const nudgeEntries: NudgeEntry[] = [
			// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
			{ actionId: 'status-updates-automation', enabled: fg('cc_automations_discovery_nudges') },
			{ actionId: 'audio-briefing', enabled: isAudioBriefingEligible },
		];

		const liveDocNudges = nudgeEntries.reduce<ConversationAction[]>((acc, nudgeEntry) => {
			if (!nudgeEntry.enabled) return acc;
			const action = isNudgeByAction(nudgeEntry)
				? nudgeEntry.action
				: actionsById.get(nudgeEntry.actionId);
			if (action) acc.push(action);
			return acc;
		}, []);

		return [...alwaysOnNudges, ...liveDocNudges];
	}, [
		sessionNudges,
		agentMentionId,
		// resolvedAgentMentionId now also gates presence (see above), so it must be a dep.
		// agentMentionContext/agentMentionName remain payload-only: agentMentionNudgeAction
		// already invalidates on those values.
		resolvedAgentMentionId,
		agentMentionNudgeAction,
		enableLiveDocNudges,
		isEligibilityLoading,
		eligibleForChangeboarding,
		isFirstVisit,
		isCurrentUserContributor,
		readTimeMinutes,
		actionsById,
	]);

	return { nudges, errors: sessionErrors };
}
